import React, { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { FileText, Loader2, Search, Send, Workflow, X } from 'lucide-react';
import {
  getApprovedWhatsAppTemplates,
  getWhatsAppFlows
} from '../../services/api/implementation/impl';

function resolveFlowApprovalStatus(flow) {
  return String(
    flow?.template_approval_status ?? flow?.template_status ?? flow?.approval_status ?? ''
  ).toUpperCase();
}

function isFlowSendable(flow) {
  const status = resolveFlowApprovalStatus(flow);
  return status === 'APPROVED' || status === 'ACTIVE';
}

/**
 * Picker for sending either an approved WhatsApp flow or a Pinggo WA template.
 */
const SendWhatsAppPicker = ({
  projectId,
  contactName,
  isSending,
  sendingKey = null,
  onClose,
  onSelectFlow,
  onSelectTemplate
}) => {
  const [sendKind, setSendKind] = useState('flow'); // 'flow' | 'template'
  const [templateSearch, setTemplateSearch] = useState('');
  const [debouncedTemplateSearch, setDebouncedTemplateSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTemplateSearch(templateSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [templateSearch]);

  const {
    data: flows = [],
    isLoading: isLoadingFlows,
    isFetching: isFetchingFlows,
    isError: isFlowsError,
    refetch: refetchFlows
  } = useQuery({
    queryKey: ['whatsappFlows', projectId],
    queryFn: () => getWhatsAppFlows(projectId),
    enabled: Boolean(projectId) && sendKind === 'flow',
    staleTime: 60_000
  });

  const {
    data: templatePayload,
    isLoading: isLoadingTemplates,
    isFetching: isFetchingTemplates,
    isError: isTemplatesError,
    refetch: refetchTemplates
  } = useQuery({
    queryKey: ['approvedWhatsAppTemplates', projectId, debouncedTemplateSearch],
    queryFn: () =>
      getApprovedWhatsAppTemplates(projectId, { search: debouncedTemplateSearch || undefined }),
    enabled: Boolean(projectId) && sendKind === 'template',
    staleTime: 60_000,
    placeholderData: keepPreviousData
  });

  const sendableFlows = useMemo(() => flows.filter(isFlowSendable), [flows]);
  const approvedTemplates = useMemo(
    () => (Array.isArray(templatePayload?.templates) ? templatePayload.templates : []),
    [templatePayload]
  );

  // Full-list spinner only on first load; keep list visible while searching / refetching.
  const showListSpinner =
    sendKind === 'template'
      ? isLoadingTemplates && !templatePayload
      : isLoadingFlows && flows.length === 0;
  const isError = sendKind === 'template' ? isTemplatesError : isFlowsError;
  const showSearchSpinner = sendKind === 'template' && isFetchingTemplates && Boolean(templatePayload);

  return (
    <div className="absolute inset-0 z-20 flex items-end justify-center bg-black/30 md:items-center">
      <div className="w-full max-w-md overflow-hidden rounded-t-2xl bg-white shadow-xl md:rounded-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900">Send WhatsApp</p>
            <p className="truncate text-xs text-gray-500">To {contactName || 'this lead'}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSending}
            className="rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40"
            aria-label="Close">
            <X className="h-4 w-4" strokeWidth={2.25} />
          </button>
        </div>

        <div className="border-b border-gray-100 px-3 py-2">
          <div className="grid grid-cols-2 gap-1 rounded-xl bg-gray-100 p-1">
            <button
              type="button"
              disabled={isSending}
              onClick={() => setSendKind('flow')}
              className={`rounded-lg px-2 py-2 text-xs font-semibold ${
                sendKind === 'flow'
                  ? 'bg-white text-[#0A4A5C] shadow'
                  : 'text-gray-600 hover:bg-white/70'
              }`}>
              Flow
            </button>
            <button
              type="button"
              disabled={isSending}
              onClick={() => setSendKind('template')}
              className={`rounded-lg px-2 py-2 text-xs font-semibold ${
                sendKind === 'template'
                  ? 'bg-white text-[#0A4A5C] shadow'
                  : 'text-gray-600 hover:bg-white/70'
              }`}>
              WA template
            </button>
          </div>
        </div>

        {sendKind === 'template' && projectId ? (
          <div className="border-b border-gray-100 px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                disabled={isSending}
                placeholder="Search templates…"
                className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-8 pr-8 text-sm text-gray-800 outline-none focus:border-[#2A565E] focus:ring-1 focus:ring-[#2A565E] disabled:opacity-50"
              />
              {showSearchSpinner ? (
                <Loader2 className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-gray-400" />
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="max-h-[60vh] overflow-auto p-2">
          {!projectId ? (
            <p className="px-3 py-8 text-center text-sm text-gray-500">
              This conversation isn’t linked to a project, so WhatsApp options can’t be listed.
            </p>
          ) : showListSpinner ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <p className="text-sm text-gray-600">
                Couldn’t load {sendKind === 'template' ? 'templates' : 'flows'}.
              </p>
              <button
                type="button"
                onClick={() => (sendKind === 'template' ? refetchTemplates() : refetchFlows())}
                className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90">
                Try again
              </button>
            </div>
          ) : sendKind === 'template' && approvedTemplates.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-gray-500">
              {debouncedTemplateSearch
                ? 'No approved templates match this search.'
                : 'No approved WhatsApp templates available for this project.'}
            </p>
          ) : sendKind === 'flow' && sendableFlows.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-gray-500">
              No approved flows available for this project. Only flows whose opener template is
              approved by Meta can be sent.
            </p>
          ) : sendKind === 'template' ? (
            <ul className="flex flex-col">
              {approvedTemplates.map((tpl) => {
                const rowBusy = isSending && sendingKey === `template:${tpl.templateName}`;
                return (
                  <li key={tpl.templateName}>
                    <button
                      type="button"
                      onClick={() =>
                        onSelectTemplate({
                          templateName: tpl.templateName
                        })
                      }
                      disabled={isSending}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                        <FileText className="h-4 w-4" strokeWidth={2} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-800">
                          {tpl.templateName}
                        </p>
                        <p className="truncate text-xs text-gray-400">
                          {[tpl.category, tpl.language].filter(Boolean).join(' · ') ||
                            'Approved template'}
                        </p>
                      </div>
                      {rowBusy ? (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      ) : (
                        <Send className="h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <ul className="flex flex-col">
              {sendableFlows.map((flow) => {
                const rowBusy = isSending && sendingKey === `flow:${flow.id}`;
                return (
                  <li key={flow.id}>
                    <button
                      type="button"
                      onClick={() => onSelectFlow(flow)}
                      disabled={isSending}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Workflow className="h-4 w-4" strokeWidth={2} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-800">
                          {flow.name || 'Untitled flow'}
                        </p>
                        {flow.description ? (
                          <p className="truncate text-xs text-gray-400">{flow.description}</p>
                        ) : null}
                      </div>
                      {rowBusy ? (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      ) : (
                        <Send className="h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {sendKind === 'flow' && isFetchingFlows && flows.length > 0 ? (
            <div className="flex justify-center py-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-300" />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default SendWhatsAppPicker;
